import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { ResultModal } from "../../EventSales/eventSalesShared";
import SearchableSelectField from "../../../../common/dashboard/SearchableSelectField";
import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  CmpEventLibraryKindEnum,
  useArchiveCmpEventLibraryItemMutation,
  useCreateCmpEventLibraryItemMutation,
  useGetCmpEventLibraryItemsQuery,
  useGetCmpManageEventsQuery,
  useUpdateCmpEventLibraryItemMutation,
} from "../../../../../graphql/types";
import { getPreviousEventTitles } from "./previousEventTitles";
import { FieldLabel, WizardActionButton, wizardTextFieldSx } from "./wizardShared";
import { buildStaffErrorFallback, getGraphQLErrorMessage, getUserFacingErrorText } from "../../../../../utils/userFacingError";

export const eventLibraryFieldClassName = "grid grid-cols-[minmax(0,1fr)] content-start gap-[6px] md:row-span-4 md:grid-rows-subgrid";

// ---------------------------------------------------------------------------
// Shared step-1 popups (verbatim per the "event title/type/code popups"
// screenshots). Exported for reuse by EventTypeSelect and the speaker flow.
// ---------------------------------------------------------------------------

// Add/Edit popup chrome: bold header + grey X, divider, white body, grey
// footer band with an outlined Cancel and a gold confirm button.
export const LibraryEditDialog = ({
  open,
  title,
  confirmLabel,
  confirmDisabled,
  maxWidth = 560,
  titleFontSize = 17,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  maxWidth?: number;
  titleFontSize?: number;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    fullWidth
    PaperProps={{ sx: { borderRadius: "14px", maxWidth, width: "calc(100% - 48px)", mx: 3 } }}
  >
    <div className="flex items-center justify-between px-[24px] py-[18px]">
      <h3 style={{ fontSize: titleFontSize }} className="font-bold leading-tight text-[#0A0A0A]">{title}</h3>
      <IconButton size="small" onClick={onClose} sx={{ bgcolor: "#F21B1B", color: "#fff", width: 28, height: 28, "&:hover": { bgcolor: "#D81717" } }} aria-label="close">
        <CloseIcon />
      </IconButton>
    </div>
    <div className="border-t border-[#ECECEC] p-[24px]">{children}</div>
    <div className="flex justify-end gap-3 rounded-b-[14px] border-t border-gray-200 bg-[#FAFAFA] px-[24px] py-[16px]">
      <WizardActionButton sx={{ minHeight: 40, fontSize: 13, px: "22px" }} tone="outline" onClick={onClose}>
        Cancel
      </WizardActionButton>
      <WizardActionButton sx={{ minHeight: 40, fontSize: 13, px: "22px" }} tone="gold" disabled={confirmDisabled} onClick={onConfirm}>
        {confirmLabel}
      </WizardActionButton>
    </div>
  </Dialog>
);

// Delete confirmation: red circular X close, big red trash circle, verbatim
// heading/body, uppercase DELETE (trash icon) + black uppercase CANCEL.
export const DeleteConfirmModal = ({
  open,
  heading,
  name,
  entity = false,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  heading: string;
  name: string;
  /** Catalogue entries (Event Title, Type, Code) read "delete the {name}". */
  entity?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth={false}
    fullWidth
    PaperProps={{ sx: { borderRadius: "14px", maxWidth: 440 } }}
  >
    <DialogContent className="relative !px-[28px] !py-[30px]">
      <IconButton
        size="small"
        onClick={onCancel}
        aria-label="close"
        className="!absolute !right-[14px] !top-[14px]"
        sx={{
          backgroundColor: "#ff1728",
          color: "#fff",
          "&:hover": { backgroundColor: "#d90f1e" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <div className="flex flex-col items-center text-center">
        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-[#ff1728] text-white">
          <DeleteOutlineIcon sx={{ fontSize: 28 }} />
        </div>
        <h3 className="mt-[18px] text-[14px] font-semibold leading-tight text-gray-950">{heading}</h3>
        {/* Claude Design (8) Create Event Screen.dc.html:1308, 2291: rows
            (speakers, partners, tickets, agenda, add-ons) read "delete
            {name}?"; catalogue entries read "delete the {name}?". The name is
            interpolated bare, with no quote marks. */}
        <p className="mt-[8px] max-w-[340px] text-[14px] leading-[1.55] text-gray-950">
          {`Are you sure you want to delete ${entity ? "the " : ""}${name}?`}
        </p>
        <div className="mt-[22px] flex w-full justify-center gap-[12px]">
          <WizardActionButton
            tone="red"
            startIcon={<DeleteOutlineIcon />}
            disabled={loading}
            onClick={onConfirm}
            sx={{ flex: 1, maxWidth: 170, minHeight: 44, fontSize: 14, borderRadius: "10px" }}
          >
            Delete
          </WizardActionButton>
          <WizardActionButton
            tone="black"
            disabled={loading}
            onClick={onCancel}
            sx={{ flex: 1, maxWidth: 170, minHeight: 44, fontSize: 14, borderRadius: "10px" }}
          >
            Cancel
          </WizardActionButton>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// Success pop-up: green check circle over a bold title, nothing else.
export const SuccessPopup = ({ open, title, highlights, onClose }: { open: boolean; title: string; highlights?: readonly string[]; onClose: () => void }): React.ReactElement => (
  <ResultModal open={open} title={title} highlights={highlights} onClose={onClose} />
);

/** Design copy: "The event title Gala has been deleted successfully." */
export const deletedMessage = (entityLabel: string, name: string) => ({
  message: `The ${entityLabel.toLowerCase()} ${name} has been deleted successfully.`,
  highlights: [name],
});

// ---------------------------------------------------------------------------
// Event Title / Event Code library select
// ---------------------------------------------------------------------------

type EventLibrarySelectProps = {
  chamberId: string;
  kind: CmpEventLibraryKindEnum;
  label: string;
  hint?: string;
  required?: boolean;
  compact?: boolean;
  placeholder?: string;
  /** Search box text inside the open list, e.g. "Search Event Title". */
  searchPlaceholder?: string;
  value: string;
  pairedValue?: string;
  attendeesPerTicket?: number;
  onChange: (value: string, pairedValue?: string | null, attendeesPerTicket?: number | null) => void;
  error?: string;
  /** Shown under the hint, in the same grid row (design Event Title checkbox). */
  hintAddon?: React.ReactNode;
  /**
   * Event Title: list the chamber's previous event names first, newest first
   * (CMP Dashboard (15) CES:1684-1692); catalogue titles not used yet follow.
   */
  previousEventTitles?: { currentEventId?: string | null };
};

// Claude Design (4) standard ticket and sponsor catalogue (CE:1674-1680),
// offered as suggestions alongside each chamber's saved entries.
const designPresetPairs: Partial<Record<CmpEventLibraryKindEnum, ReadonlyArray<{ value: string; pairedValue: string }>>> = (() => {
  const tickets: ReadonlyArray<[string, string]> = [
    ["Member Ticket", "MT"], ["Non-Member Ticket", "NMT"], ["Table of 8", "T8"],
    ["Table of 10", "T10"], ["No Charge Sponsorship Ticket (Member Ticket)", "NCST"], ["Complimentary Ticket", "CT"],
  ];
  const sponsors: ReadonlyArray<[string, string]> = [
    ["Platinum Sponsor", "PLAT"], ["Gold Sponsor", "GOLD"], ["Silver Sponsor", "SILV"],
    ["Bronze Sponsor", "BRNZ"], ["Title Sponsor", "TITL"], ["Supporting Sponsor", "SUPP"],
  ];
  return {
    TICKET_NAME: tickets.map(([name, code]) => ({ value: name, pairedValue: code })),
    TICKET_CODE: tickets.map(([name, code]) => ({ value: code, pairedValue: name })),
    SPONSOR_NAME: sponsors.map(([name, code]) => ({ value: name, pairedValue: code })),
    SPONSOR_CODE: sponsors.map(([name, code]) => ({ value: code, pairedValue: name })),
  } as Partial<Record<CmpEventLibraryKindEnum, ReadonlyArray<{ value: string; pairedValue: string }>>>;
})();

/**
 * Seats per ticket are set by the system (the design has no seats input):
 * "Table of 8" / code "T8" / "8 Seats" give 8; anything else is one seat.
 */
export const deriveTicketSeats = (name?: string | null, code?: string | null): number => {
  const fromName = /table of\s+(\d{1,3})/i.exec(name || "") || /(\d{1,3})\s*seats?\b/i.exec(name || "");
  const fromCode = /^T(\d{1,3})$/i.exec((code || "").trim());
  const seats = Number((fromName || fromCode)?.[1] || 1);
  return Number.isInteger(seats) && seats >= 1 ? seats : 1;
};

const noun = (kind: CmpEventLibraryKindEnum) => ({ TITLE: "Event Title", CODE: "Event Code", TICKET_NAME: "Ticket Name", TICKET_CODE: "Ticket Code", SPONSOR_NAME: "Sponsor Name", SPONSOR_CODE: "Sponsor Code" }[kind]);
const catalogueErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: ["BAD_USER_INPUT"] });
const shortNoun = (kind: CmpEventLibraryKindEnum) => noun(kind).split(" ").slice(-1)[0];
const codeKinds = [CmpEventLibraryKindEnum.Code, CmpEventLibraryKindEnum.TicketCode, CmpEventLibraryKindEnum.SponsorCode];
const inputMaxLength = (kind: CmpEventLibraryKindEnum) => codeKinds.includes(kind) ? 4 : kind === CmpEventLibraryKindEnum.Title ? 120 : 100;

// Ticket and sponsor names and codes are saved as one-to-one pairs by the API.
const counterpartKinds: Partial<Record<CmpEventLibraryKindEnum, CmpEventLibraryKindEnum>> = {
  [CmpEventLibraryKindEnum.TicketName]: CmpEventLibraryKindEnum.TicketCode,
  [CmpEventLibraryKindEnum.TicketCode]: CmpEventLibraryKindEnum.TicketName,
  [CmpEventLibraryKindEnum.SponsorName]: CmpEventLibraryKindEnum.SponsorCode,
  [CmpEventLibraryKindEnum.SponsorCode]: CmpEventLibraryKindEnum.SponsorName,
};

/** Same key normalization as the API catalogue (cap-backend cmpEventLibraryPairs.ts). */
export const normalizeCatalogueValue = (value?: string | null): string =>
  (value || "").normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();

type CatalogueEntry = { value: string; pairedValue?: string | null };
const findCatalogueEntry = (entries: readonly CatalogueEntry[], value: string) =>
  entries.find((entry) => normalizeCatalogueValue(entry.value) === normalizeCatalogueValue(value));
/** The entry that already owns `counterpart` as its pair, if it is not `value` itself. */
const findOtherPairOwner = (entries: readonly CatalogueEntry[], value: string, counterpart: string) =>
  normalizeCatalogueValue(counterpart)
    ? entries.find((entry) => normalizeCatalogueValue(entry.pairedValue) === normalizeCatalogueValue(counterpart)
      && normalizeCatalogueValue(entry.value) !== normalizeCatalogueValue(value))
    : undefined;

export const pairConflictMessage = (counterpartKind: CmpEventLibraryKindEnum, counterpart: string, owner: string, value: string): string =>
  `${shortNoun(counterpartKind)} "${counterpart}" is already used by "${owner}". Enter a new ${shortNoun(counterpartKind).toLowerCase()} for "${value}" or pick "${owner}".`;
const catalogueConflictFallback = (kind: CmpEventLibraryKindEnum): string => {
  const entity = [CmpEventLibraryKindEnum.SponsorName, CmpEventLibraryKindEnum.SponsorCode].includes(kind) ? "sponsor" : "ticket";
  return `This ${entity} name or code is already paired with a different ${entity}. Pick the existing ${entity} from the list, or use a new name and code.`;
};
const isCataloguePairConflict = (message: string) => /already used by another catalogue pair/i.test(message);

/** Plain-language text for the API pair-conflict error, naming the conflict when the catalogue shows it. */
export const describeCatalogueConflict = (kind: CmpEventLibraryKindEnum, entries: readonly CatalogueEntry[], value: string, counterpart: string): string => {
  const counterpartKind = counterpartKinds[kind];
  const owner = counterpartKind ? findOtherPairOwner(entries, value, counterpart) : undefined;
  if (counterpartKind && owner) return pairConflictMessage(counterpartKind, counterpart.trim(), owner.value, value.trim());
  const existing = findCatalogueEntry(entries, value);
  if (counterpartKind && existing) {
    return `${shortNoun(kind)} "${existing.value}" is already saved with a different ${shortNoun(counterpartKind).toLowerCase()}. Pick "${existing.value}" from the list.`;
  }
  return catalogueConflictFallback(kind);
};

const EventLibrarySelect = ({
  chamberId,
  kind,
  label,
  hint,
  required,
  compact,
  placeholder,
  searchPlaceholder,
  value,
  onChange, pairedValue, attendeesPerTicket,
  error,
  hintAddon,
  previousEventTitles,
}: EventLibrarySelectProps): React.ReactElement => {
  const { data: manageEventsData } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId || !previousEventTitles,
    fetchPolicy: "cache-first",
  });
  const leadingValues = useMemo(
    () => previousEventTitles
      ? getPreviousEventTitles(manageEventsData?.getCmpManageEvents.events || [], previousEventTitles.currentEventId)
      : [],
    [manageEventsData, previousEventTitles],
  );
  const { data, refetch } = useGetCmpEventLibraryItemsQuery({
    variables: { input: { chamberId, kind, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: catalogueErrorContext,
  });
  const items = useMemo(
    () => data?.getCmpEventLibraryItems.items || [],
    [data]
  );
  const selected = items.find((item) => item.value === value);
  const presets = designPresetPairs[kind] || [];
  const savedValues = new Set(items.map((item) => item.value.toLowerCase()));
  const unsavedPresets = presets.filter((entry) => !savedValues.has(entry.value.toLowerCase()));
  // Event, Ticket and Sponsor Codes are optional, so a selected code can be
  // cleared, as in Manage Members (UpdateMember "Not specified").
  const leading = Array.from(new Map(
    (leadingValues || [])
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => [entry.toLowerCase(), entry] as const),
  ).values());
  const leadingKeys = new Set(leading.map((entry) => entry.toLowerCase()));
  const notLeading = (entry: string) => !leadingKeys.has(entry.trim().toLowerCase());
  const options = [
    ...(value && codeKinds.includes(kind) ? [{ value: "", label: "Not specified" }] : []),
    ...(value && !selected && notLeading(value) && !presets.some((entry) => entry.value === value) ? [{ value, label: value }] : []),
    ...leading.map((entry) => ({ value: entry, label: entry })),
    ...items.filter((item) => notLeading(item.value)).map((item) => ({ value: item.value, label: item.value })),
    ...unsavedPresets.filter((entry) => notLeading(entry.value)).map((entry) => ({ value: entry.value, label: entry.value })),
  ];
  const counterpartKind = counterpartKinds[kind];
  const catalogueField = Boolean(counterpartKind);
  // Ticket and Sponsor Codes are optional (Claude Design (8) Create Event
  // Screen.dc.html:1341, 1411), so a name can be saved without a code. The
  // name side of every pair stays required.
  const counterpartOptional = counterpartKind === CmpEventLibraryKindEnum.TicketCode
    || counterpartKind === CmpEventLibraryKindEnum.SponsorCode;

  const [createItem, { loading: creating }] =
    useCreateCmpEventLibraryItemMutation({ context: catalogueErrorContext });
  const [updateItem, { loading: updating }] =
    useUpdateCmpEventLibraryItemMutation({ context: catalogueErrorContext });
  const [archiveItem, { loading: archiving }] =
    useArchiveCmpEventLibraryItemMutation({ context: catalogueErrorContext });

  const [dialog, setDialog] = useState<"add" | "edit" | null>(null);
  const [draftValue, setDraftValue] = useState("");
  // Shown only when the other field's value belongs to a different pair.
  const [counterpartDraft, setCounterpartDraft] = useState<string | null>(null);
  const [failure, setFailure] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [result, setResult] = useState<{ message: string; highlights: string[] } | null>(null);
  // Edit or Delete with nothing selected (Create Event Screen.dc.html:1826-1829).
  const [selectionError, setSelectionError] = useState("");

  const openAdd = () => {
    setDraftValue(""); setCounterpartDraft(null); setFailure(""); setSelectionError("");
    setDialog("add");
  };
  const requireSelection = () => {
    if (value) return true;
    setSelectionError(`Select an ${noun(kind)} first.`);
    return false;
  };
  const openEdit = () => {
    if (!requireSelection()) {
      return;
    }
    setSelectionError("");
    setDraftValue(selected?.value || value); setFailure("");
    setDialog("edit");
  };

  // Ticket names and codes are saved as pairs with one seat count, so both
  // kinds derive seats from the pair and otherwise keep the ticket's seats.
  // A pair typed in the dialog is a new ticket, so it does not keep the seats
  // of the conflicting pair that was selected before.
  const ticketSeatsFor = (entryValue: string, pair = pairedValue, newPair = false): number | undefined => {
    if (kind !== CmpEventLibraryKindEnum.TicketName && kind !== CmpEventLibraryKindEnum.TicketCode) return undefined;
    const name = kind === CmpEventLibraryKindEnum.TicketName ? entryValue : pair;
    const code = kind === CmpEventLibraryKindEnum.TicketCode ? entryValue : pair;
    const derived = deriveTicketSeats(name, code);
    return derived > 1 || newPair ? derived : attendeesPerTicket || 1;
  };

  const selectOption = (next: string) => {
    if (!next) {
      // "Not specified" clears only this code. The name and seats stay.
      setSelectionError("");
      onChange("", null, null);
      return;
    }
    const item = items.find(entry => entry.value === next);
    const preset = presets.find(entry => entry.value === next);
    // A name saved with no pair (null) keeps a code typed for this ticket, but
    // not a code that belongs to another name (the previously selected one).
    const pair = item?.pairedValue ?? preset?.pairedValue
      ?? (counterpartKind && next !== value && findOtherPairOwner([...items, ...unsavedPresets], next, pairedValue || "") ? "" : undefined);
    const seats = kind === CmpEventLibraryKindEnum.TicketName || kind === CmpEventLibraryKindEnum.TicketCode
      ? item?.attendeesPerTicket ?? deriveTicketSeats(kind === CmpEventLibraryKindEnum.TicketName ? next : pair, kind === CmpEventLibraryKindEnum.TicketCode ? next : pair)
      : item?.attendeesPerTicket;
    setSelectionError("");
    onChange(next, pair, seats);
  };

  const describeConflict = async (entryValue: string, pair: string) => {
    let fresh: readonly CatalogueEntry[] = [...items, ...unsavedPresets];
    try {
      const reloaded = (await refetch())?.data?.getCmpEventLibraryItems.items;
      if (reloaded) {
        const reloadedValues = new Set(reloaded.map(item => item.value.toLowerCase()));
        fresh = [...reloaded, ...presets.filter(entry => !reloadedValues.has(entry.value.toLowerCase()))];
      }
    } catch { /* Keep the loaded catalogue when the reload fails. */ }
    return describeCatalogueConflict(kind, fresh, entryValue, pair);
  };

  const handleSave = async () => {
    const entryValue = draftValue.trim();
    if (!entryValue) {
      return;
    }
    setFailure("");
    const pair = (counterpartDraft ?? pairedValue ?? "").trim();
    if (dialog === "add" && counterpartKind) {
      // An existing name or code is selected with its saved pair, not created again.
      const existing = findCatalogueEntry([...items, ...unsavedPresets], entryValue);
      if (existing) {
        selectOption(existing.value);
        setDialog(null);
        return;
      }
      // The other field's value belongs to a different pair: ask for a new one.
      const owner = findOtherPairOwner([...items, ...unsavedPresets], entryValue, pair);
      if (owner || (counterpartDraft !== null && !pair && !counterpartOptional)) {
        setCounterpartDraft(counterpartDraft ?? "");
        setFailure(owner
          ? pairConflictMessage(counterpartKind, pair, owner.value, entryValue)
          : `Enter a ${shortNoun(counterpartKind).toLowerCase()} for "${entryValue}".`);
        return;
      }
    }
    try {
      if (dialog === "add" || !selected) {
        const created = await createItem({
          variables: { input: { chamberId, kind, value: entryValue, pairedValue: counterpartKind ? pair : pairedValue,
            attendeesPerTicket: ticketSeatsFor(entryValue, pair, counterpartDraft !== null) } },
        });
        await refetch();
        const createdValue = created.data?.createCmpEventLibraryItem?.item?.value;
        if (!createdValue) throw new Error("The catalogue entry could not be saved.");
        onChange(createdValue, created.data?.createCmpEventLibraryItem.item?.pairedValue, created.data?.createCmpEventLibraryItem.item?.attendeesPerTicket);
      } else if (dialog === "edit" && selected) {
        const result = await updateItem({
          variables: {
            input: { chamberId, itemId: selected.id, value: draftValue.trim(), pairedValue: selected.pairedValue,
              // Renaming keeps the saved seat count unless the new name states one.
              attendeesPerTicket: (ticketSeatsFor(draftValue) || 1) > 1 ? ticketSeatsFor(draftValue) : selected.attendeesPerTicket,
              expectedUpdatedAtUtcMilli: selected.updatedAtUtcMilli },
          },
        });
        await refetch();
        if (!result.data?.updateCmpEventLibraryItem.item) throw new Error("The catalogue entry could not be saved.");
        onChange(draftValue.trim(), result.data?.updateCmpEventLibraryItem.item?.pairedValue, result.data?.updateCmpEventLibraryItem.item?.attendeesPerTicket);
      }
      setDialog(null);
    } catch (cause) {
      // The pair-conflict check reads the server text only (no Apollo wrapper).
      const message = getGraphQLErrorMessage(cause) || "";
      if (counterpartKind && isCataloguePairConflict(message)) {
        setFailure(await describeConflict(entryValue, pair));
      } else {
        setFailure(getUserFacingErrorText(cause, buildStaffErrorFallback(`save the ${noun(kind)}`)));
      }
    }
  };

  const handleDelete = async () => {
    if (!selected) {
      return;
    }
    try {
      const result = await archiveItem({
        variables: { input: { chamberId, itemId: selected.id, expectedUpdatedAtUtcMilli: selected.updatedAtUtcMilli } },
      });
      if (!result.data?.archiveCmpEventLibraryItem.itemSavedSuccessfully) throw new Error("The catalogue entry could not be deleted.");
      await refetch();
      onChange("");
      setConfirmDelete(false);
      setResult(deletedMessage(noun(kind), selected.value));
    } catch (cause) { setConfirmDelete(false); setFailure(getUserFacingErrorText(cause, buildStaffErrorFallback(`delete the ${noun(kind)}`))); }
  };

  // Event Title and Event Code show "This field is required" and the
  // empty-selection message beside the buttons, not under the dropdown
  // (Create Event Screen.dc.html:116-121, 151-156).
  const inlineErrors = !catalogueField;
  return (
    <div className={eventLibraryFieldClassName}>
      <FieldLabel required={required} hint={hintAddon ? undefined : hint} compact={compact}
        fontSize={[CmpEventLibraryKindEnum.SponsorName, CmpEventLibraryKindEnum.SponsorCode].includes(kind) ? 13 : undefined}
        // Grey note after the label (Create Event Screen.dc.html:148, 1341, 1411).
        note={codeKinds.includes(kind) ? "- Max 4 characters (Optional)" : undefined}
        className={`contents ${!catalogueField || [CmpEventLibraryKindEnum.SponsorName, CmpEventLibraryKindEnum.SponsorCode].includes(kind) ? "[&>span]:leading-[normal]" : ""}`}
        hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]">
        {label}
      </FieldLabel>
      {hintAddon ? (
        // Create Event Screen.dc.html:114-120: hint and checkbox, gap 10px.
        <div className="flex flex-col gap-[10px]">
          {hint ? <p className="text-[13px] leading-[normal] text-[#0A0A0A]">{hint}</p> : null}
          {hintAddon}
        </div>
      ) : null}
      <SearchableSelectField id={`event-library-${kind}`} value={value} error={inlineErrors ? undefined : error} invalid={inlineErrors && Boolean(error)} placeholder={placeholder || `Select ${noun(kind)}`}
        searchPlaceholder={searchPlaceholder}
        options={options}
        onChange={selectOption} />
      <div className={`flex flex-wrap gap-[8px] ${!catalogueField ? "items-center [&_.MuiButton-root]:!min-h-[26px] [&_.MuiButton-root]:!px-[10px]" : ""}`}>
        <WizardActionButton startIcon={catalogueField ? <AddIcon /> : undefined} tone={kind === CmpEventLibraryKindEnum.Title || kind === CmpEventLibraryKindEnum.Code ? "black" : "gold"} size="small" onClick={openAdd}>
          {/* "Add New Event Title/Code", "Add New Ticket Name/Code", "Add New
              Sponsor Name/Code" (Create Event Screen.dc.html:117, 152, 1338, 1344, 1408, 1414). */}
          Add New {noun(kind)}
        </WizardActionButton>
        {(kind === CmpEventLibraryKindEnum.Title || kind === CmpEventLibraryKindEnum.Code) && <>
        <WizardActionButton
          tone="gold"
          size="small"
          onClick={openEdit}
        >
          Edit
        </WizardActionButton>
        <WizardActionButton
          tone="red"
          size="small"
          onClick={() => {
            if (!requireSelection()) return;
            setSelectionError("");
            if (selected) setConfirmDelete(true);
          }}
        >
          Delete
        </WizardActionButton>
        </>}
        {inlineErrors && error ? <span className="text-[12px] text-[#F0202E]">This field is required</span> : null}
        {inlineErrors && selectionError && !value ? <span className="text-[12px] text-[#F0202E]">{selectionError}</span> : null}
        {failure && !dialog && <p role="alert" className="w-full text-sm text-red-600">{failure}</p>}
      </div>

      <LibraryEditDialog
        open={Boolean(dialog)}
        title={dialog === "edit" ? `Edit ${noun(kind)}` : `Add ${noun(kind)}`}
        confirmLabel={
          `${dialog === "edit" ? "Edit" : "Save"} ${catalogueField ? noun(kind) : shortNoun(kind)}`
        }
        confirmDisabled={creating || updating || !draftValue.trim() || (counterpartDraft !== null && !counterpartDraft.trim() && !counterpartOptional)}
        onConfirm={handleSave}
        onClose={() => setDialog(null)}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={`Add ${noun(kind)}`}
          value={draftValue}
          inputProps={{ maxLength: inputMaxLength(kind), "aria-label": noun(kind) }}
          onChange={({ target }) => setDraftValue(target.value)}
          sx={wizardTextFieldSx}
        />
        {counterpartKind && counterpartDraft !== null && <TextField
          fullWidth
          size="small"
          placeholder={`Add ${noun(counterpartKind)}`}
          value={counterpartDraft}
          inputProps={{ maxLength: inputMaxLength(counterpartKind), "aria-label": noun(counterpartKind) }}
          onChange={({ target }) => setCounterpartDraft(target.value)}
          sx={{ ...wizardTextFieldSx, mt: "12px" }}
        />}
        {failure && <p role="alert" className="mt-3 text-sm text-red-600">{failure}</p>}
      </LibraryEditDialog>

      <DeleteConfirmModal
        open={confirmDelete}
        heading={`Delete ${noun(kind)}`}
        entity
        name={selected?.value || value}
        loading={archiving}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <SuccessPopup
        open={Boolean(result)}
        title={result?.message || ""}
        highlights={result?.highlights}
        onClose={() => setResult(null)}
      />
    </div>
  );
};

export default EventLibrarySelect;
